import Card from "@ui-kit/data-display/Card/Card.web";
import Button from "@ui-kit/form/Button/Button.web";
import RadioGroup from "@ui-kit/form/RadioGroup/RadioGroup.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useState } from "react";
import SeatOptionsRadio from "./SeatOptionsRadio/SeatOptionsRadio.web";
import { SeatRemoveModalProps, SeatType } from "./SeatRemoveModal.props";
import { SeatActionRequiredAlert } from "../SeatActionRequiredAlert/SeatActionRequiredAlert.web";
import { useSeatRemoveModal } from "./useSeatRemoveModal";

const SeatRemoveModal = (props: SeatRemoveModalProps) => {
  const { isOpen, onClose, seatsToRemove } = props;
  const { t } = useTranslation("seating");
  const { originalSeat } = seatsToRemove;

  const [seats, setSeats] = useState<SeatType[]>([]);
  const updateSeats = (input: Array<SeatType>) => {
    setSeats(input);
  };

  // use this to store the user selection for which seat to remove
  // example: for some isolated seats, users can choose to remove 1 of multiple seats
  const [selectedSeat, setSelectedSeat] = useState<SeatType>();
  const updateSelectedSeat = (inputSeat: SeatType) => {
    setSelectedSeat(inputSeat);
  };

  const { confirmRemoval, getDescriptionText } = useSeatRemoveModal({
    ...props,
    seats,
    updateSeats,
    selectedSeat,
    updateSelectedSeat,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="3xl"
      scrollBehavior="inside"
    >
      <Modal.Header>
        {t("Section {{section}}, Row {{row}}, Seat {{seat}}", {
          section: originalSeat.segment_identifier,
          row: originalSeat.location_identifier,
          seat: originalSeat.identifier,
        })}
      </Modal.Header>
      <Modal.Body>
        <Flex
          data-testid="seat-removal-modal"
          w="full"
          direction="column"
          gap="md"
        >
          {/* Displays an explanation to the user (alert with title and subtitle) */}
          <SeatActionRequiredAlert {...getDescriptionText()} />
          {seats.length > 1 && selectedSeat ? (
            // Display radio options, allow users to choose seats to remove
            <RadioGroup
              value={selectedSeat.seatId}
              onChange={(value) => {
                const seat = seats.find((s) => s.seatId === Number(value));
                if (seat) updateSelectedSeat(seat);
              }}
              display="flex"
              flexDirection="column"
              gap="md"
            >
              {seats?.map((seat, index) => {
                return (
                  <SeatOptionsRadio
                    key={seat?.$data?.id}
                    currentSeat={seat}
                    index={index}
                    selectedSeat={selectedSeat}
                    updateSelectedSeat={updateSelectedSeat}
                  />
                );
              })}
            </RadioGroup>
          ) : (
            // Display all seats to be removed
            seats?.map((seat) => (
              <Card
                key={seat?.$data?.id}
                w="full"
                border="1px solid"
                borderColor="gray.400"
                rounded="sm"
              >
                <Text color="font.dark">
                  <span>{t("Section")} </span>
                  <strong>{seat?.$data?.segment_identifier}</strong>
                  <span>, {t("Row")} </span>
                  <strong>{seat?.$data?.location_identifier}</strong>
                  <span>, {t("Seat")} </span>
                  <strong>{seat?.$data?.identifier}</strong>
                </Text>
              </Card>
            ))
          )}
        </Flex>
      </Modal.Body>
      <Modal.Footer
        borderTop="1px solid"
        borderColor="gray.400"
        flexDirection="row"
      >
        <Flex justifyContent="space-between">
          <Button.Ghost
            data-testid="seat-remove-modal-cancel"
            onClick={onClose}
          >
            {t("Cancel", { ns: "common" })}
          </Button.Ghost>
        </Flex>
        <Button
          data-testid="seat-remove-modal-continue"
          minWidth="element.7xl"
          variant="solid"
          colorPalette="primary"
          disabled={!seats || !seats.length}
          onClick={confirmRemoval}
        >
          {t("Continue", { ns: "common" })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SeatRemoveModal;
